import { useRef, useState } from 'react';
import { Download, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import api from '../lib/api';
import { downloadExport } from './ExportMenu';

/**
 * Import Excel/CSV dengan pratinjau, "semua atau tidak sama sekali".
 *
 * Alur: Unduh Template -> pilih file -> pratinjau (server memvalidasi setiap
 * baris dengan aturan yang sama dengan form) -> tombol Import hanya aktif bila
 * tidak ada error -> server memvalidasi ULANG saat commit.
 * Ikon: data masuk = panah ke bawah (Download), template = FileSpreadsheet.
 */
export function ImportDialog({ type, title, onDone, label = 'Import', size = 'sm', className = '', testId }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const tid = testId || `import-${type}`;

  const reset = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const errorText = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && d.message) return d.message;
    if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
    return fallback;
  };

  const choose = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post(`/import/${type}/preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
    } catch (err) {
      toast.error(errorText(err, 'File tidak bisa dibaca'));
      reset();
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!file || !preview?.valid) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/import/${type}/commit`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(res.data.message);
      setOpen(false);
      reset();
      onDone && onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object' && Array.isArray(d.errors)) {
        setPreview((p) => ({ ...(p || {}), valid: false, errors: d.errors, error_count: d.errors.length }));
      }
      toast.error(errorText(err, 'Import gagal'));
    } finally {
      setBusy(false);
    }
  };

  const cols = preview?.preview?.length ? Object.keys(preview.preview[0]).filter((k) => k !== 'row') : [];
  const fmt = (v) => (typeof v === 'number' ? v.toLocaleString('id-ID') : String(v ?? ''));

  return (
    <>
      <Button size={size} variant="outline" className={className} onClick={() => { reset(); setOpen(true); }} data-testid={tid}>
        <Download className="h-4 w-4 mr-1" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) reset(); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid={`${tid}-dialog`}>
          <DialogHeader>
            <DialogTitle>Import {title}</DialogTitle>
            <DialogDescription>
              Unduh template, isi sheet <strong>Data</strong>, lalu pilih file (.xlsx atau .csv). Data hanya disimpan bila
              <strong> semua baris valid</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => downloadExport(`/import/${type}/template`, {}, `template_import_${type}.xlsx`)}
              data-testid={`${tid}-template`}>
              <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Unduh Template
            </Button>
            <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${tid}-choose`}>
              {busy && !preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              {file ? 'Ganti File' : 'Pilih File'}
            </Button>
            <input ref={inputRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={choose} data-testid={`${tid}-file`} />
            {file && <span className="self-center text-sm text-slate-600 truncate max-w-[16rem]">{file.name}</span>}
          </div>

          {preview && (
            <div className="space-y-3" data-testid={`${tid}-preview`}>
              {preview.valid ? (
                <div className="flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" /> {preview.total} baris valid dan siap diimport.
                </div>
              ) : (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800 space-y-2" data-testid={`${tid}-errors`}>
                  <div className="flex items-center gap-2 font-medium">
                    <AlertTriangle className="h-4 w-4" /> {preview.error_count} error di file — perbaiki lalu pilih file lagi. Tidak ada data yang disimpan.
                  </div>
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-left"><th className="pr-3">Baris</th><th className="pr-3">Kolom</th><th>Masalah</th></tr></thead>
                      <tbody>
                        {preview.errors.map((e, i) => (
                          <tr key={i} className="align-top border-t border-red-100">
                            <td className="pr-3 py-1 font-mono">{e.row}</td><td className="pr-3 py-1">{e.kolom}</td><td className="py-1">{e.pesan}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {cols.length > 0 && (
                <div className="overflow-x-auto border rounded-md max-h-64">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>{['Baris', ...cols].map((c) => <th key={c} className="px-2 py-1.5 text-left whitespace-nowrap">{c}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((r) => (
                        <tr key={r.row} className="border-t">
                          <td className="px-2 py-1 font-mono">{r.row}</td>
                          {cols.map((c) => <td key={c} className="px-2 py-1 whitespace-nowrap">{fmt(r[c])}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Tutup</Button>
            <Button onClick={commit} disabled={busy || !preview?.valid} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`${tid}-commit`}>
              {busy && preview ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Import {preview?.valid ? `${preview.total} data` : ''}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ImportDialog;
